import { Box, styled, Typography } from "@mui/material";

export const StyledItemsTableFrame = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.custom.white,
  border: `1px solid ${theme.palette.custom.lightPink}`,
  borderRadius: theme.borderRadius?.radius?.wallet ?? 8,
  overflow: "hidden",
  boxSizing: "border-box",
}));

export const StyledItemsTableHeader = styled(Box)(({ theme }) => ({
  padding: "12px 12px 10px 14px",
  backgroundColor: theme.palette.custom.white,
  borderBottom: `1px solid ${theme.palette.custom.lightPink}`,
  position: "relative",
  zIndex: 2,
  isolation: "isolate",
}));

export const StyledItemsTableScroll = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$compact",
})(({ theme, $compact }) => ({
  width: "100%",
  maxHeight: $compact ? 240 : 300,
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  padding: "4px 12px 12px 14px",
  boxSizing: "border-box",
  backgroundColor: theme.palette.custom.white,
  position: "relative",
  zIndex: 1,
  "&::-webkit-scrollbar": {
    width: 8,
    height: 8,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#d0d7de",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f5f5f5",
  },
}));

export const StyledOrderItemsTable = styled("table")(() => ({
  width: "100%",
  minWidth: 600,
  borderCollapse: "separate",
  borderSpacing: 0,
  tableLayout: "fixed",
}));

export const StyledOrderItemsColGroup = styled("colgroup")(() => ({}));

export const StyledOrderItemsThead = styled("thead")(({ theme }) => ({
  backgroundColor: theme.palette.custom.white,
  position: "sticky",
  top: 0,
  zIndex: 10,
  "& th": {
    backgroundColor: theme.palette.custom.white,
    borderBottom: `1px solid ${theme.palette.custom.lightPink}`,
  },
}));

const columnMinWidths = {
  product: 130,
  qty: 44,
  weight: 80,
  mode: 120,
  status: 180,
};

const columnCellPadding = {
  product: { pl: 0, pr: 12 },
  qty: { pl: 8, pr: 8 },
  weight: { pl: 8, pr: 20 },
  mode: { pl: 12, pr: 28 },
  status: { pl: 20, pr: 8 },
};

export const StyledOrderItemsTh = styled("th", {
  shouldForwardProp: (prop) => prop !== "$align" && prop !== "$column",
})(({ theme, $align = "left", $column = "product" }) => {
  const pad = columnCellPadding[$column] || columnCellPadding.product;
  const minW = columnMinWidths[$column];
  return {
    padding: `12px ${pad.pr}px 10px ${pad.pl}px`,
    fontSize: theme.typography.small.fontSize,
    fontWeight: theme.typography.fontWeight.semiBold,
    color: theme.palette.custom.lockerValueColor,
    textAlign: $align,
    verticalAlign: "bottom",
    minWidth: minW ? `${minW}px` : undefined,
    whiteSpace:
      $column === "qty" || $column === "weight" || $column === "mode" || $column === "status"
        ? "nowrap"
        : undefined,
    overflow: $column === "mode" ? "hidden" : undefined,
  };
});

export const StyledOrderItemsTbody = styled("tbody")(() => ({
  position: "relative",
  zIndex: 0,
}));

export const StyledOrderItemsTr = styled("tr")(({ theme }) => ({
  backgroundColor: theme.palette.custom.white,
  "& td": {
    backgroundColor: theme.palette.custom.white,
    borderBottom: `1px solid ${theme.palette.custom.lightPink}`,
  },
  "&:last-of-type td": {
    borderBottom: "none",
  },
}));

export const StyledOrderItemsTd = styled("td", {
  shouldForwardProp: (prop) => prop !== "$align" && prop !== "$column",
})(({ theme, $align = "left", $column = "product" }) => {
  const pad = columnCellPadding[$column] || columnCellPadding.product;
  const minW = columnMinWidths[$column];
  return {
    padding: `14px ${pad.pr}px 14px ${pad.pl}px`,
    verticalAlign: "middle",
    textAlign: $align,
    fontSize: theme.typography.regular.fontSize,
    minWidth: minW ? `${minW}px` : undefined,
    whiteSpace:
      $column === "qty" || $column === "weight" || $column === "mode" || $column === "status"
        ? "nowrap"
        : undefined,
    overflow: $column === "mode" ? "hidden" : $column === "status" ? "visible" : undefined,
    [theme.breakpoints.down("sm")]: {
      padding: `12px ${Math.max(pad.pr - 4, 8)}px 12px ${Math.max(pad.pl - 4, 4)}px`,
    },
  };
});

export const StyledItemsTableFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 8,
  marginTop: 14,
  paddingTop: 12,
  paddingLeft: 2,
  borderTop: `1px solid ${theme.palette.custom.lightPink}`,
  fontSize: theme.typography.small.fontSize,
  color: theme.palette.custom.lockerValueColor,
}));

export const StyledModeCell = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.regular.fontSize,
  color: theme.palette.custom.blackColor,
  textAlign: "left",
  lineHeight: 1.35,
  fontWeight: theme.typography.fontWeight.medium,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  display: "block",
  boxSizing: "border-box",
}));

export const StyledProductCell = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  minWidth: 0,
  overflow: "hidden",
  position: "relative",
  zIndex: 0,
}));

export const StyledProductImage = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$compact",
})(({ theme, $compact }) => ({
  width: $compact ? 56 : 72,
  height: $compact ? 56 : 72,
  borderRadius: theme.borderRadius.radius.wallet,
  border: `1px solid ${theme.palette.custom.lightPink}`,
  background: theme.palette.custom.white,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
  zIndex: 0,
  [theme.breakpoints.down("sm")]: {
    width: 48,
    height: 48,
  },
}));

export const StyledProductName = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.regular.fontSize,
  fontWeight: theme.typography.fontWeight.medium,
  color: theme.palette.custom.primary,
  lineHeight: 1.4,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  wordBreak: "break-word",
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
    opacity: 0.8,
  },
}));

export const StyledCellValue = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.regular.fontSize,
  color: theme.palette.custom.blackColor,
  textAlign: "right",
  fontWeight: theme.typography.fontWeight.medium,
}));

export const StyledWeightCell = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.regular.fontSize,
  color: theme.palette.custom.blackColor,
  fontWeight: theme.typography.fontWeight.medium,
  textAlign: "right",
  lineHeight: 1.5,
  whiteSpace: "nowrap",
  display: "block",
}));

export const StyledEmptyState = styled(Box)(({ theme }) => ({
  padding: "32px 16px",
  textAlign: "center",
  color: theme.palette.custom.notProvide,
}));
